// src/encounter/risk/services/risk-assessment-queries.ts
//
// The Fall / Pain reads, as one set of query keys and query options shared by the Risk card's editor hook (use-risk-assessments.ts)
// and the pre-visit "Notes from nurse" card's read-only hook (services/use-nurse-note.ts): same key, same fetcher, same cached shape
// ({ entries, version }), so the two never fetch twice or disagree. Kept in its own module so a suite that mocks the editor hook
// still gets these.
import { getFallAssessments, getPainAssessments } from '@/shared/api/risk-assessment-service';
import type { FallAssessmentEntry, PainAssessmentEntry } from '../lib/risk-assessment-types';

export const riskAssessmentKeys = {
  fall: (encounterId: string | null) => ['risk-fall-assessments', encounterId] as const,
  pain: (encounterId: string | null) => ['risk-pain-assessments', encounterId] as const,
};

/** One list as cached: the entries and the version the server sent with them (PROD-3142). */
export interface RiskList<T> {
  entries: T[];
  version: string | undefined;
}

export function fallQueryOptions(encounterId: string | null, enabled = true) {
  return {
    queryKey: riskAssessmentKeys.fall(encounterId),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<RiskList<FallAssessmentEntry>> => {
      let version: string | undefined;
      const entries = await getFallAssessments(encounterId as string, { signal, onVersion: (v) => (version = v) });
      return { entries, version };
    },
    enabled: enabled && !!encounterId,
  };
}

export function painQueryOptions(encounterId: string | null, enabled = true) {
  return {
    queryKey: riskAssessmentKeys.pain(encounterId),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<RiskList<PainAssessmentEntry>> => {
      let version: string | undefined;
      const entries = await getPainAssessments(encounterId as string, { signal, onVersion: (v) => (version = v) });
      return { entries, version };
    },
    enabled: enabled && !!encounterId,
  };
}
